import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography, Collapse } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PregnantWomanRoundedIcon from "@mui/icons-material/PregnantWomanRounded";

const LeftSidebarMenu = () => {
  const location = useLocation();

  const isSettingActive = location.pathname.startsWith("/dashboard/settings");
  const isReportsActive =
    location.pathname.startsWith("/dashboard/reports") ||
    location.pathname.startsWith("/reports");

  const [settingsExpanded, setSettingsExpanded] = useState(isSettingActive);
  const [reportsExpanded, setReportsExpanded] = useState(isReportsActive);
  const [clientsReportOpen, setClientsReportOpen] = useState(isReportsActive);
  const [diagnosisOpen, setDiagnosisOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/dashboard/settings")) setSettingsExpanded(true);
    if (location.pathname.startsWith("/dashboard/reports") || location.pathname.startsWith("/reports")) {
      setReportsExpanded(true);
      setClientsReportOpen(true);
    }
  }, [location.pathname]);

  const bgNavy = "#0A2540";
  const textMuted = "#8EA5B9";
  const textWhite = "#FFFFFF";
  const activePillBg = "#15365A";
  const hoverBg = "rgba(255, 255, 255, 0.05)";

  const mainNavigation = [
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: <GridViewRoundedIcon sx={{ fontSize: "1.25rem" }} />,
    },
    {
      label: "Patient",
      path: "/dashboard/patients",
      icon: <PersonOutlineOutlinedIcon sx={{ fontSize: "1.25rem" }} />,
    },
    {
      label: "Clinical",
      path: "/dashboard/clinical",
      icon: <AddBoxOutlinedIcon sx={{ fontSize: "1.25rem" }} />,
    },
    {
      label: "Scheduler",
      path: "/dashboard/scheduler",
      icon: <CalendarTodayOutlinedIcon sx={{ fontSize: "1.25rem" }} />,
    },
    {
      label: "Claim",
      path: "/dashboard/claims",
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: "1.25rem" }} />,
    },
    {
      label: "Visits",
      path: "/dashboard/visits",
      icon: <CalendarMonthOutlinedIcon sx={{ fontSize: "1.25rem" }} />,
    },
    {
      label: "Payment",
      path: "/dashboard/payments",
      icon: <CreditCardOutlinedIcon sx={{ fontSize: "1.25rem" }} />,
    },
  ];

  const reportSubItems = [
    { label: "Clients", path: "/dashboard/reports/clients" },
    { label: "Caseload", path: "/dashboard/reports/caseload" },
    { label: "Visits Reports", path: "/dashboard/reports/visits-reports" },
    { label: "Staff Session", path: "/dashboard/reports/staff-session" },
    { label: "Staff Login His...", path: "/dashboard/reports/staff-login-history" },
    { label: "Clinical", path: "/dashboard/reports/clinical" },
    { label: "Missing Servic...", path: "/dashboard/reports/missing-services" },
    { label: "Staff Detail", path: "/dashboard/reports/staff-detail" },
    { label: "Clinical Doc Hi...", path: "/dashboard/reports/clinical-doc-history" },
    { label: "Client Count", path: "/dashboard/reports/client-count" },
    { label: "Account Recei...", path: "/dashboard/reports/account-receivable" },
    { label: "Time Overlap", path: "/dashboard/reports/time-overlap" },
    { label: "Payment List", path: "/dashboard/reports/payment-list" },
    
  ];

  return (
    <Box
      sx={{
        width: "235px",
        minWidth: "235px",
        maxWidth: "235px",
        height: "100vh",
        backgroundColor: bgNavy,
        borderRadius: "22px 0 0 22px",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflow: "hidden",
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Top Header */}
      <Box sx={{ flexShrink: 0, px: 2, pt: 2.5, pb: 1.2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1.2 }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <Box sx={{ width: 80, height: 66, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg viewBox="0 0 100 80" fill="#FFFFFF" style={{ width: "100%", height: "100%" }}>
                <path d="M 46 12 C 40 4, 28 4, 22 10 C 14 8, 6 16, 8 26 C 2 30, 0 38, 4 46 C 0 54, 4 64, 14 66 C 18 74, 28 76, 36 72 C 42 76, 46 72, 46 68 Z" />
                <path d="M 54 12 C 60 4, 72 4, 78 10 C 86 8, 94 16, 92 26 C 98 30, 100 38, 96 46 C 100 54, 96 64, 86 66 C 82 74, 72 76, 64 72 C 58 76, 54 72, 54 68 Z" />
              </svg>
            </Box>
          </Link>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ width: "95px", height: "1px", backgroundColor: "rgba(255, 255, 255, 0.16)", margin: "0 auto 10px" }} />
          <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: textWhite, lineHeight: 1.2 }}>
            Awais11
          </Typography>
          <Typography sx={{ fontSize: "0.76rem", color: textMuted, fontWeight: 400, mt: 0.3 }}>
            awais@gmail.com
          </Typography>
        </Box>
      </Box>

      {/* Navigation List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1.6,
          pb: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: "3.5px",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {mainNavigation.map((item, index) => {
          const isActive = location.pathname === item.path;
          return (
            <Link key={index} to={item.path} style={{ textDecoration: "none" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.6,
                  px: 1.8,
                  py: 0.95,
                  borderRadius: "8px",
                  backgroundColor: isActive ? activePillBg : "transparent",
                  color: isActive ? textWhite : textMuted,
                  transition: "all 0.15s ease-in-out",
                  "&:hover": {
                    backgroundColor: isActive ? activePillBg : hoverBg,
                    color: textWhite,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", color: isActive ? textWhite : textMuted }}>
                  {item.icon}
                </Box>
                <Typography sx={{ fontSize: "0.86rem", fontWeight: isActive ? 700 : 500, letterSpacing: "0.1px" }}>
                  {item.label}
                </Typography>
              </Box>
            </Link>
          );
        })}

        {/* Setting Dropdown */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            onClick={() => setSettingsExpanded((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.8,
              py: 0.95,
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: isSettingActive ? activePillBg : "transparent",
              color: isSettingActive ? textWhite : textMuted,
              transition: "all 0.15s ease-in-out",
              "&:hover": { backgroundColor: isSettingActive ? activePillBg : hoverBg, color: textWhite },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.6 }}>
              <SettingsOutlinedIcon sx={{ fontSize: "1.25rem" }} />
              <Typography sx={{ fontSize: "0.86rem", fontWeight: isSettingActive ? 700 : 500 }}>
                Setting
              </Typography>
            </Box>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: "1.15rem",
                transform: settingsExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.25s ease",
              }}
            />
          </Box>

          <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", pl: 2.8, pt: 0.5 }}>
              <Link to="/dashboard/settings/practice" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/practice" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/practice" ? activePillBg : "transparent" }}>
                  <BusinessOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Practice Setting</Typography>
                </Box>
              </Link>
              <Link to="/dashboard/settings/insurance" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/insurance" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/insurance" ? activePillBg : "transparent" }}>
                  <ShieldOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Insurance</Typography>
                </Box>
              </Link>
              <Link to="/dashboard/settings/user" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/user" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/user" ? activePillBg : "transparent" }}>
                  <PersonOutlineOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>User</Typography>
                </Box>
              </Link>

              <Box>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setDiagnosisOpen((prev) => !prev);
                  }}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.2, py: "6px", borderRadius: "6px", cursor: "pointer", color: location.pathname.includes("/diagnosis") ? textWhite : textMuted, backgroundColor: location.pathname.includes("/diagnosis") ? activePillBg : "transparent" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <MedicalServicesOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Diagnosis</Typography>
                  </Box>
                  <ChevronRightRoundedIcon sx={{ fontSize: "1.05rem", transform: diagnosisOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
                </Box>

                <Collapse in={diagnosisOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", pl: 2, pt: 0.3 }}>
                    <Link to="/dashboard/settings/diagnosis/icd" style={{ textDecoration: "none" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: "5px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/diagnosis/icd" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/diagnosis/icd" ? activePillBg : "transparent" }}>
                        <AddBoxOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                        <Typography sx={{ fontSize: "0.74rem", fontWeight: 500 }}>ICD</Typography>
                      </Box>
                    </Link>
                    <Link to="/dashboard/settings/diagnosis/obgyn" style={{ textDecoration: "none" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: "5px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/diagnosis/obgyn" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/diagnosis/obgyn" ? activePillBg : "transparent" }}>
                        <PregnantWomanRoundedIcon sx={{ fontSize: "0.95rem" }} />
                        <Typography sx={{ fontSize: "0.74rem", fontWeight: 500 }}>OBGYN Dataset</Typography>
                      </Box>
                    </Link>
                  </Box>
                </Collapse>
              </Box>

              <Link to="/dashboard/settings/medicines" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/medicines" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/medicines" ? activePillBg : "transparent" }}>
                  <MedicationOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Medicines</Typography>
                </Box>
              </Link>
              <Link to="/dashboard/settings/provider" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/provider" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/provider" ? activePillBg : "transparent" }}>
                  <SecurityOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Provider</Typography>
                </Box>
              </Link>
              <Link to="/dashboard/settings/procedure" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/procedure" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/procedure" ? activePillBg : "transparent" }}>
                  <FactCheckOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Procedure</Typography>
                </Box>
              </Link>
              <Link to="/dashboard/settings/facilities" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/facilities" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/facilities" ? activePillBg : "transparent" }}>
                  <LocalHospitalOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Facilities</Typography>
                </Box>
              </Link>
              <Link to="/dashboard/settings/practices" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/practices" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/practices" ? activePillBg : "transparent" }}>
                  <CloseFullscreenOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Practices</Typography>
                </Box>
              </Link>
              <Link to="/dashboard/settings/modules" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/modules" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/modules" ? activePillBg : "transparent" }}>
                  <GridViewOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Modules</Typography>
                </Box>
              </Link>
              <Link to="/dashboard/settings/courses" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, px: 1.2, py: "6px", borderRadius: "6px", color: location.pathname === "/dashboard/settings/courses" ? textWhite : textMuted, backgroundColor: location.pathname === "/dashboard/settings/courses" ? activePillBg : "transparent" }}>
                  <SchoolOutlinedIcon sx={{ fontSize: "1.05rem" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Course</Typography>
                </Box>
              </Link>
            </Box>
          </Collapse>
        </Box>

        {/* Reports Dropdown */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            onClick={() => setReportsExpanded((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.8,
              py: 0.95,
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: isReportsActive ? activePillBg : "transparent",
              color: isReportsActive ? textWhite : textMuted,
              transition: "all 0.15s ease-in-out",
              "&:hover": { backgroundColor: isReportsActive ? activePillBg : hoverBg, color: textWhite },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.6 }}>
              <BarChartRoundedIcon sx={{ fontSize: "1.25rem" }} />
              <Typography sx={{ fontSize: "0.86rem", fontWeight: isReportsActive ? 700 : 500 }}>
                Reports
              </Typography>
            </Box>
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: "1.15rem",
                transform: reportsExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.25s ease",
              }}
            />
          </Box>

          <Collapse in={reportsExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", pl: 2.8, pt: 0.4 }}>
              <Box
                onClick={() => setClientsReportOpen((prev) => !prev)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: "8px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  color: isReportsActive ? textWhite : textMuted,
                  backgroundColor: isReportsActive ? activePillBg : "transparent",
                  "&:hover": { backgroundColor: hoverBg, color: textWhite },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
                  <PeopleAltOutlinedIcon sx={{ fontSize: "1.15rem" }} />
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>Clients Report</Typography>
                </Box>
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    fontSize: "1.1rem",
                    transform: clientsReportOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </Box>

              <Collapse in={clientsReportOpen} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", pl: 2, pt: 0.3 }}>
                  {reportSubItems.map((sub, sIdx) => {
                    const isSubActive = location.pathname === sub.path;
                    return (
                      <Link key={sIdx} to={sub.path} style={{ textDecoration: "none" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.4,
                            px: 1.4,
                            py: "6px",
                            borderRadius: "6px",
                            color: isSubActive ? textWhite : textMuted,
                            backgroundColor: isSubActive ? activePillBg : "transparent",
                            transition: "all 0.15s ease",
                            "&:hover": { backgroundColor: hoverBg, color: textWhite },
                          }}
                        >
                          <ReceiptLongOutlinedIcon sx={{ fontSize: "1.1rem", flexShrink: 0 }} />
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: isSubActive ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {sub.label}
                          </Typography>
                        </Box>
                      </Link>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};

export default LeftSidebarMenu;